import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { LabeledStat } from '@/components/common/StatCardGrid';
import type { JobCardQueryOptionsFn } from '../api/attendance.api';

interface UseJobCardParams {
  queryOptions: JobCardQueryOptionsFn;
  dateFrom: string;
  dateTo: string;
  search: string;
  isActive?: boolean | undefined;
}

export function useJobCard({ queryOptions, dateFrom, dateTo, search, isActive }: UseJobCardParams) {
  const {
    data = [],
    isLoading,
    error,
    refetch,
  } = useQuery(queryOptions({ dateFrom, dateTo, isActive }));

  const filtered = useMemo(() => {
    const q = search.trim().replace(/\s+/gu, ' ').toLowerCase();
    if (!q) return data;

    return data.filter((item) => {
      const haystack = [
        item.zmName,
        item.amName,
        item.zmUsername,
        item.zmMobile,
        item.zmHrEmpId,
        item.thana,
        item.district,
      ];
      return haystack.some((v) => v?.toLowerCase().includes(q) ?? false);
    });
  }, [data, search]);

  const summary = useMemo((): LabeledStat[] => {
    const totals = filtered.reduce(
      (acc, item) => ({
        present: acc.present + item.totalPresentDays,
        absent: acc.absent + item.totalAbsentDays,
        weekends: acc.weekends + item.totalWeekends,
      }),
      { present: 0, absent: 0, weekends: 0 },
    );
    return [
      { key: 'users', label: 'Users', value: filtered.length },
      { key: 'present', label: 'Present', value: totals.present },
      { key: 'absent', label: 'Absent', value: totals.absent },
      { key: 'weekends', label: 'Weekends', value: totals.weekends },
    ];
  }, [filtered]);

  return { filtered, summary, isLoading, error, refetch };
}
